import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { toast } from 'react-toastify';
import { getUsers } from '../commonApi';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { Loader } from "../Loader";
import CreateUserModal from "./CreateUserModal";

// Web app users are created here (Keycloak admin portal realm, roles ADMIN/OPERATOR/QA);
// mobile app users register from the app and are view-only here.
export const USER_TYPE_LABELS = {
  web: "Web App Users",
  mobile: "Mobile App Users",
};

// Opened from the sidebar: Users > Web App Users (/users/web) or Mobile App Users (/users/mobile).
function Usergrid({ userType = "web" }) {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [columnName, setColumnName] = useState("id");
  const [countPerPage, setCountPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [userData, setUserData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setCreateOpen] = useState(false);
  // searchInput follows the text box; search is what is sent to the API (updated after typing stops).
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const expectedPageRef = useRef(0);

  // Fetch user data from the common API
  const fetchData = async () => {
    setLoading(true);
    try {
      const propertyNameMap = {
        "ID": "id",
        "First Name": "firstName",
        "Last Name": "lastName",
        "Email": "email",
      };

      let propertyName = "id";
      if (sortColumn) {
        propertyName = propertyNameMap[sortColumn] || sortColumn.toLowerCase().replace(/ /g, "_");
      }
      const sortOrderFlag = sortOrder === "asc" ? "ASC" : "DESC";
      setColumnName(propertyName);

      const response = await getUsers(page, countPerPage, propertyName, sortOrderFlag, { search, userType });

      if (response && response.data && response.data.content) {
        const adminUsers = response.data.content;
        setUserData(adminUsers);
        setTotalRows(response.data.totalElements || 0);
      } else {
        setUserData([]);
        setTotalRows(0);
      }
    } catch (error) {
      toast.error("Failed to fetch user data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        if (!isCancelled) {
          await fetchData();
        }
      } catch (error) {
        if (!isCancelled) {
          toast.error("Failed to fetch user data");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      isCancelled = true;
    };
  }, [page, countPerPage, sortColumn, sortOrder, search, userType]);

  // Search on the server (name or email) 400 ms after the user stops typing, from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim();
      if (term !== search) {
        setSearch(term);
        setPage(0);
        expectedPageRef.current = 0;
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handlePerRowsChange = (newPerPage) => {
    setCountPerPage(newPerPage);
    setPage(0);
    expectedPageRef.current = 0;
  };

  const handleSort = (column, sortDirection) => {
    if (!column || !column.name) {
      return;
    }
    setSortColumn(column.name);
    setSortOrder(sortDirection);
    setPage(0);
    expectedPageRef.current = 0;
  };

  const columns = [
    // Columns share the full table width. Rows are still keyed by id (DataTable's default keyField).
    { name: "First Name", selector: (row) => row?.firstName, sortable: true, grow: 1, minWidth: "140px" },
    { name: "Last Name", selector: (row) => row?.lastName, sortable: true, grow: 1, minWidth: "140px" },
    { name: "Email", selector: (row) => row?.email, sortable: true, grow: 2, minWidth: "240px" },
    {
      name: "Roles",
      grow: 1,
      minWidth: "140px",
      // Only web users have ADMIN/OPERATOR/QA roles; mobile app roles are not meaningful here.
      omit: userType !== "web",
      cell: (row) => (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {(row?.roles || []).map((role) => (
            <span key={role} className="badge rounded-pill text-bg-light border">{role}</span>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="custom_tab_wrapper">
      <div className="tab-content">
        <div className="tab-pane fade show active">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button className="nav-link active" type="button">
                {USER_TYPE_LABELS[userType] || USER_TYPE_LABELS.web} : {totalRows}
              </button>
            </li>
          </ul>

          <div className="tab-pane fade show active">
            <div className="tab_content_wrapper full_height_tab">
              <div className="custom_table_action">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search by Name or Email"
                  aria-label="Search users"
                  style={{ width: "240px" }}
                  value={searchInput}
                  onChange={handleSearchInputChange}
                />
                {/* Only web app users can be created from here; mobile users register from the app. */}
                {userType === "web" && (
                  <OverlayTrigger placement="left" overlay={<Tooltip id="tooltip-add-user">Add New User</Tooltip>}>
                    <button className="btn primary_btn" type="button" aria-label="Add New User" onClick={() => setCreateOpen(true)}>
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </OverlayTrigger>
                )}
              </div>

              <div className="table-responsive h-100vh-150px">
                {loading ? (
                  <div className="spin-loader">
                    <Loader />
                  </div>
                ) : (
                  <div className="custom_table">
                    <DataTable
                      columns={columns}
                      data={userData}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={countPerPage}
                      paginationDefaultPage={page + 1}
                      onChangeRowsPerPage={handlePerRowsChange}
                      onChangePage={(newPage) => {
                        setPage(newPage - 1);
                        expectedPageRef.current = newPage - 1;
                      }}
                      onSort={handleSort}
                      sortServer
                      highlightOnHover
                      fixedHeader
                      fixedHeaderScrollHeight="440px"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {isCreateOpen && (
            <CreateUserModal
              onClose={() => setCreateOpen(false)}
              // Reload page 1 so the new user (stored by Keycloak) shows up in the list.
              onCreated={() => {
                if (page === 0) {
                  fetchData();
                } else {
                  setPage(0);
                  expectedPageRef.current = 0;
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Usergrid;